import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../shared/Modal';
import customAgentService from '../../services/customAgent';
import './TemplateGallery.css';

const templates = [
  {
    id: 'competitor-alert',
    name: 'Competitor Alert',
    description: 'Automatically generate battle cards and notify team when competitors are mentioned',
    category: 'Sales Intelligence',
    icon: '🎯',
    useCase: 'When a competitor is identified in a client conversation, this customAgent generates a battle card and sends a Slack notification to your team.',
    nodeCount: 4
  },
  {
    id: 'account-brief-email',
    name: 'Account Brief → Email Draft',
    description: 'Generate an AI-powered email draft when an account brief is created',
    category: 'Communication',
    icon: '📧',
    useCase: 'When you create an account brief, this customAgent automatically generates a personalized email and sends it via SendGrid.',
    nodeCount: 4
  },
  {
    id: 'deal-stage-notification',
    name: 'Deal Stage Change Alert',
    description: 'Send notifications when deals move between stages',
    category: 'Sales Ops',
    icon: '📊',
    useCase: 'Stay informed about deal progression. Get instant notifications when deals move to critical stages.',
    nodeCount: 6
  },
  {
    id: 'daily-pipeline-report',
    name: 'Daily Pipeline Report',
    description: 'Automated daily summary of your sales pipeline',
    category: 'Reporting',
    icon: '📈',
    useCase: 'Get a comprehensive daily report of your pipeline delivered to your inbox every morning.',
    nodeCount: 4
  },
  {
    id: 'new-lead-enrichment',
    name: 'New Lead Enrichment',
    description: 'Automatically enrich and qualify new leads',
    category: 'Lead Management',
    icon: '🚀',
    useCase: 'When a new lead is created, automatically enrich company data and notify the assigned rep.',
    nodeCount: 4
  }
];

const TemplateGallery = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);

  const categories = ['All', ...new Set(templates.map(t => t.category))];

  const filteredTemplates = selectedCategory === 'All'
    ? templates
    : templates.filter(t => t.category === selectedCategory);

  const handleUseTemplate = async (template) => {
    try {
      setLoading(true);
      
      // Fetch full template data from backend (would need API endpoint)
      // For now, create a new customAgent with template data
      const customAgentData = {
        name: `${template.name} (from template)`,
        description: template.description,
        nodes: [], // Would be populated from template
        edges: [],
        executionMode: 'manual',
        status: 'draft'
      };

      const response = await customAgentService.createCustomAgent(customAgentData);
      
      // Navigate to the new customAgent
      navigate(`/wizard/custom-agents/${response.data.id}`);
      onClose();
    } catch (error) {
      console.error('Error using template:', error);
      alert('Failed to create customAgent from template');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="CustomAgent Templates"
        size="xlarge"
      >
        <div className="template-gallery">
          {/* Category Filters */}
          <div className="template-filters">
            {categories.map(category => (
              <button
                key={category}
                className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Template Grid */}
          <div className="template-grid">
            {filteredTemplates.map(template => (
              <div key={template.id} className="template-card">
                <div className="template-icon">{template.icon}</div>
                
                <div className="template-content">
                  <h4>{template.name}</h4>
                  <p className="template-description">{template.description}</p>
                  
                  <div className="template-meta">
                    <span className="template-category">{template.category}</span>
                    <span className="template-nodes">{template.nodeCount} nodes</span>
                  </div>
                </div>

                <div className="template-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => handlePreview(template)}
                  >
                    Preview
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => handleUseTemplate(template)}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Use Template'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="template-empty">
              <p>No templates found in this category</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Template Preview Modal */}
      {selectedTemplate && (
        <Modal
          isOpen={!!selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          title={selectedTemplate.name}
          size="large"
        >
          <div className="template-preview">
            <div className="preview-header">
              <span className="preview-icon">{selectedTemplate.icon}</span>
              <span className="preview-category">{selectedTemplate.category}</span>
            </div>

            <div className="preview-section">
              <h5>Description</h5>
              <p>{selectedTemplate.description}</p>
            </div>

            <div className="preview-section">
              <h5>Use Case</h5>
              <p>{selectedTemplate.useCase}</p>
            </div>

            <div className="preview-section">
              <h5>CustomAgent Details</h5>
              <ul className="preview-details">
                <li>Nodes: {selectedTemplate.nodeCount}</li>
                <li>Category: {selectedTemplate.category}</li>
                <li>Execution Mode: Configurable</li>
              </ul>
            </div>

            <div className="preview-actions">
              <button
                className="btn-secondary"
                onClick={() => setSelectedTemplate(null)}
              >
                Close
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  handleUseTemplate(selectedTemplate);
                  setSelectedTemplate(null);
                }}
                disabled={loading}
              >
                Use This Template
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default TemplateGallery;

